import { add, scale, viewDir } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, applyDamage, applyHeal, applyImpulse, applyStatus, playersInRadius, isEnemy } from "../../sim/combat.ts";
import { action, commonInput, consume, contactEnemies, grab, knockUp, ready, release, secs, startMove, trigger, useUlt } from "../kitlib.ts";
import { fxk, type HeroModule } from "../module.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";

export const V = { rage: 0, animal: 1, regen: 2, berserk: 3 } as const;
export const FX = { Claw: fxk(HeroId.Woolverine, 0), Leap: fxk(HeroId.Woolverine, 1), Slam: fxk(HeroId.Woolverine, 2) } as const;

const KIND_RAMPAGE = MoveKind.Hero;
const KIND_LEAP = MoveKind.Hero + 1;
const KIND_ULT = MoveKind.Hero + 2;

const def: HeroDef = {
  id: HeroId.Woolverine,
  slug: "woolverine",
  name: "Woolverine",
  role: Role.Damage,
  color: "#f5d000",
  blurb: "Feral tank-buster who builds rage, shrugs off pressure, and drags prey out of formation.",
  health: 350,
  armor: 0,
  shields: 0,
  height: 1.7,
  radius: 0.42,
  headRadius: 0.22,
  speed: 7,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 2800,
  abilities: {
    [Slot.Primary]: { name: "Wooly Claws", key: "LMB", description: "Slash in a four-hit chain, dealing flat and max-health damage that scales with rage.", cooldown: 0 },
    [Slot.Secondary]: { name: "Tufted Rampage", key: "RMB", description: "Dash 7.5 m and slash the first enemy touched.", cooldown: 3 },
    [Slot.Ability1]: { name: "Feral Fetch", key: "Shift", description: "Leap 20 m. The first enemy hit is grabbed and dragged down.", cooldown: 12 },
    [Slot.Ability2]: { name: "Unshorn Animal", key: "E", description: "Reduce incoming damage for 5 seconds.", cooldown: 16 },
    [Slot.Ultimate]: { name: "Last Shear", key: "Q", description: "Uppercut nearby enemies, then slam forward for rage-scaling damage.", cooldown: 0 },
  },
  passives: [
    { name: "Baa-serker Rage", description: "Dealing and taking damage builds rage up to 100, increasing claw damage." },
    { name: "Knitted Healing Factor", description: "At critical health, gain 300 bonus health, then convert leftovers into healing." },
  ],
};

function addRage(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, amount: number) {
  setHv(sim.c, eid, V.rage, Math.min(100, hv(sim.c, eid, V.rage) + amount));
}

function clawDamage(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, target: number, base = 18, maxPct = 0.015, ragePct = 0.00062) {
  const rage = hv(sim.c, eid, V.rage);
  return base + sim.c.Health.maxHp[target]! * (maxPct + rage * ragePct);
}

function slash(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, range: number, rage: number, base = 18, maxPct = 0.015, ragePct = 0.00062) {
  let hit = false;
  contactEnemies(sim, eid, range, (t) => {
    hit = true;
    applyDamage(sim, t, clawDamage(sim, eid, t, base, maxPct, ragePct), eid);
    addRage(sim, eid, rage);
    sim.emit({ type: "impact", pos: add(sim.posOf(t), { x: 0, y: sim.hero(t).height * 0.55, z: 0 }), kind: FX.Claw });
  });
  return hit;
}

function startHeroMove(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, kind: MoveKind, yaw: number, seconds: number, speed: number, y = 0) {
  const d = viewDir(yaw, 0);
  startMove(sim, eid, kind, secs(seconds), speed, { x: d.x * speed, y, z: d.z * speed });
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.animal) > 0) {
        setHv(c, eid, V.animal, hv(c, eid, V.animal) - 1);
        applyStatus(sim, eid, "resist", 0.1, { resistPct: 0.4, source: eid });
      }
      if (hv(c, eid, V.berserk) > 0) setHv(c, eid, V.berserk, hv(c, eid, V.berserk) - 1);
      const regenCd = hv(c, eid, V.regen);
      if (regenCd > 0) setHv(c, eid, V.regen, regenCd - 1);
      if (c.Health.overTicks[eid] === 1 && c.Health.over[eid]! > 0) applyHeal(sim, eid, eid, c.Health.over[eid]!, { noUlt: true });
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(1.1);
        const center = add(sim.posOf(eid), { x: 0, y: 1, z: 0 });
        for (const { eid: t } of playersInRadius(sim, center, 8, (e) => isEnemy(sim, eid, e), false)) {
          applyDamage(sim, t, 50, eid);
          knockUp(sim, t, 9);
          addRage(sim, eid, 25);
        }
        startHeroMove(sim, eid, KIND_ULT, input.yaw, 0.55, 30, 5);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.Cast, 8);
        startHeroMove(sim, eid, KIND_RAMPAGE, input.yaw, 7.5 / 44, 44);
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Cast, 12);
        startHeroMove(sim, eid, KIND_LEAP, input.yaw, 0.75, 26, 8);
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        setHv(c, eid, V.animal, secs(5));
        action(sim, eid, ActionKind.Cast, 18);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        const berserk = hv(c, eid, V.berserk) > 0;
        c.Weapon.fireCd[eid] = berserk ? secs(1 / 5.88) : secs(0.27);
        action(sim, eid, ActionKind.Melee, 8);
        if (berserk) slash(sim, eid, 3.5, 5, 8, 0.01, 0.00035);
        else slash(sim, eid, 3.5, 10);
      }
    },
    scripted(sim, eid, _input, kind) {
      if (kind === KIND_RAMPAGE && slash(sim, eid, 3, 15, 0, 0.015, 0.00057)) sim.c.Move.ticks[eid] = 0;
      if (kind === KIND_LEAP) {
        contactEnemies(sim, eid, 1.25, (t) => {
          if (sim.c.Status.held[t]! > 0) return;
          applyDamage(sim, t, 35, eid);
          addRage(sim, eid, 10);
          setHv(sim.c, eid, V.berserk, secs(5));
          grab(sim, eid, t, {
            offset: { x: 0, y: 0.1, z: -1.1 },
            seconds: 0.8,
            onRelease: (s, target) => {
              if (s.players.has(eid) && s.players.has(target)) {
                applyDamage(s, target, 35, eid);
                applyImpulse(s, target, scale(viewDir(s.c.Look.yaw[eid]!, 0), 6));
                s.emit({ type: "explosion", pos: s.posOf(target), radius: 2.5, kind: FX.Slam });
              }
            },
          });
          sim.c.Move.ticks[eid] = Math.min(sim.c.Move.ticks[eid]!, secs(0.35));
        });
      }
      if (kind === KIND_ULT) {
        const center = add(sim.posOf(eid), { x: 0, y: 0.8, z: 0 });
        for (const { eid: t } of playersInRadius(sim, center, 8, (e) => isEnemy(sim, eid, e), false)) {
          applyDamage(sim, t, 50 + sim.c.Health.maxHp[t]! * (0.1 + hv(sim.c, eid, V.rage) * 0.003), eid);
          applyImpulse(sim, t, { x: 0, y: -8, z: 0 });
        }
        sim.emit({ type: "explosion", pos: center, radius: 8, kind: FX.Slam });
        release(sim, eid);
        sim.c.Move.ticks[eid] = 0;
      }
    },
    onDamaged(sim, eid, amount) {
      addRage(sim, eid, 8);
      return amount;
    },
    onLethal(sim, eid) {
      if (hv(sim.c, eid, V.regen) > 0) return false;
      const bonus = 300;
      sim.c.Health.hp[eid] = 1;
      addOverhealth(sim, eid, bonus, secs(3), eid);
      setHv(sim.c, eid, V.rage, 0);
      setHv(sim.c, eid, V.regen, secs(90));
      sim.emit({ type: "explosion", pos: add(sim.posOf(eid), { x: 0, y: 0.8, z: 0 }), radius: 3, kind: FX.Slam });
      return true;
    },
  },
  fx: {
    [FX.Claw]: { color: "#f5d000" },
    [FX.Leap]: { color: "#fff0a0" },
    [FX.Slam]: { color: "#ff8a3d" },
  },
};

export default module;
